import type {OptimisticTask, TaskType, UnhydratedTask} from '../types/task'

export const REPO_TASKS_PAGE_SIZE = 10

export type TaskCounts = {
  open: number
  closed: number
  needsAttention?: number
}

export type TaskList = Array<UnhydratedTask | OptimisticTask>

/**
 * The value stored in the task list query caches.
 */
export interface TaskListPage {
  tasks: TaskList
  hasNextPage: boolean
  counts?: TaskCounts
}

/** Read the task list out of a cached page, defaulting to an empty list. */
export function readTasks(page: TaskListPage | undefined): TaskList {
  return page?.tasks ?? []
}

/** Return a new page with the given task list, preserving the rest of the page. */
export function writeTasks(page: TaskListPage | undefined, tasks: TaskList): TaskListPage {
  return {hasNextPage: false, ...page, tasks}
}

/**
 * Null-safe map over a cached page's tasks, preserving `hasNextPage`/`counts`.
 * Returns the original value untouched when the cache entry is empty.
 */
export function mapPageTasks(
  page: TaskListPage | undefined,
  fn: (task: TaskList[number]) => TaskList[number],
): TaskListPage | undefined {
  if (!page) return page
  return {...page, tasks: page.tasks.map(fn)}
}

/** Null-safe patch of a single task (matched by id) within a cached page. */
export function updateTaskInPage(
  page: TaskListPage | undefined,
  taskId: string | undefined,
  patch: Partial<TaskList[number]>,
): TaskListPage | undefined {
  return mapPageTasks(page, task => (task.id === taskId ? {...task, ...patch} : task))
}

/** Null-safe removal of a single task (matched by id) from a cached page. */
export function removeTaskFromPage(
  page: TaskListPage | undefined,
  taskId: string | undefined,
): TaskListPage | undefined {
  if (!page) return page
  return {...page, tasks: page.tasks.filter(task => task.id !== taskId)}
}

/**
 * When we have an optimistic task causing overflow, limit to page size to prevent UI shift when the real task replaces it.
 */
export function preventOptimisticOverflow<T extends {isOptimistic?: boolean}>(tasks: T[]): T[] {
  if (tasks.length > REPO_TASKS_PAGE_SIZE) {
    const hasOptimisticTask = tasks.some(task => task.isOptimistic)
    if (hasOptimisticTask) {
      return tasks.slice(0, REPO_TASKS_PAGE_SIZE)
    }
  }
  return tasks
}

export const repoTasksQueryKey = (
  owner: string,
  repo: string,
  creatorIds?: number[],
  page?: number,
  archived?: boolean,
  taskType?: TaskType,
  agentIds?: number[],
  customAgentName?: string,
  statuses?: string[],
  sort?: string,
  query?: string,
  agentSlugs?: string[],
) => {
  const key: unknown[] = ['repo-tasks', owner, repo, creatorIds, page, archived]
  if (taskType !== undefined) key.push(taskType)
  if (agentIds !== undefined && agentIds.length > 0) key.push(...agentIds)
  if (customAgentName !== undefined) key.push(customAgentName)
  if (statuses !== undefined && statuses.length > 0) key.push(`statuses:${statuses.join(',')}`)
  if (sort !== undefined && sort !== 'newest') key.push(`sort:${sort}`)
  if (query !== undefined && query !== '') key.push(`q:${query}`)
  if (agentSlugs !== undefined && agentSlugs.length > 0) key.push(`agents:${agentSlugs.join(',')}`)
  return key
}
